import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** Enforce monotonicity: realized values must never decrease over time */
function enforceMonotonicity(points: CurvaSFisicaPoint[]): CurvaSFisicaPoint[] {
  if (points.length === 0) return points;
  let maxRealized = 0;
  let maxPlanned = 0;
  return points.map(p => {
    if (p.curva_realizada != null) {
      maxRealized = Math.max(maxRealized, p.curva_realizada);
    }
    if (p.curva_planejada != null) {
      maxPlanned = Math.max(maxPlanned, p.curva_planejada);
    }
    return {
      ...p,
      curva_realizada: p.curva_realizada != null ? maxRealized : null,
      curva_planejada: p.curva_planejada != null ? maxPlanned : null,
    };
  });
}

export interface CurvaSFisicaPoint {
  data: string;
  curva_planejada: number | null;
  curva_realizada: number | null;
  p10: number | null;
  p50: number | null;
  p80: number | null;
  fonte: string | null;
}

export function useCurvaSFisica(contratoId: string | null) {
  const { data: points = [], isLoading } = useQuery({
    queryKey: ['curva-s-fisica', contratoId],
    queryFn: async () => {
      if (!contratoId) return [];

      const { data, error } = await supabase
        .from('emp_vw_curva_s_completa' as any)
        .select('*')
        .eq('contrato_id', contratoId)
        .order('data', { ascending: true });

      if (error) {
        console.log('[CURVA-S-FISICA] Falling back to base view', { contratoId, error: error.message });
        // Fallback to base view without projections
        const { data: fallback, error: fbErr } = await supabase
          .from('emp_vw_curva_s_fisica_contrato' as any)
          .select('*')
          .eq('contrato_id', contratoId)
          .order('data', { ascending: true });
        if (fbErr) return [];
        return ((fallback || []) as any[]).map(p => ({
          ...p,
          p10: null, p50: null, p80: null, fonte: 'historico',
        })) as CurvaSFisicaPoint[];
      }

      // Apply monotonic enforcement: realized curve should never decrease
      const raw = (data || []) as unknown as CurvaSFisicaPoint[];
      const result = enforceMonotonicity(raw);
      
      console.log('[CURVA-S-FISICA] SOURCE_SELECTED', {
        contratoId,
        pontos: result.length,
        temPlanejada: result.some(p => p.curva_planejada != null),
        temRealizada: result.some(p => p.curva_realizada != null),
        temProjecao: result.some(p => p.p50 != null),
      });

      return result;
    },
    enabled: !!contratoId,
    staleTime: 3 * 60 * 1000,
  });

  return { points, isLoading };
}
